'use client';

import React, { forwardRef } from 'react';

const stats = [
  {
    psi: '3,000',
    psiLabel: 'PSI',
    headline: 'What 3,000 PSI does to black mold',
    body:
      'North-facing walls accumulate mold in 90 days during humid months. At 3,000 PSI with hot-water injection, a 200 sq ft wall face is clean in under 8 minutes. Left untreated, that mold penetrates vinyl siding in 18 months — replacement cost: $4,200.',
    tag: 'Surface Science',
    color: 'var(--accent-blue)',
  },
  {
    psi: '94%',
    psiLabel: '',
    headline: 'of HOA violations are surface-related',
    body:
      'Algae staining, oil residue, and efflorescence account for 94% of HOA compliance notices in multi-unit complexes. A single pressure-wash session resolves the violation — and avoids the $500–$2,000 fine that follows the second notice.',
    tag: 'HOA Data',
    color: '#22c55e',
  },
  {
    psi: '6×',
    psiLabel: '',
    headline: 'faster than chemical treatment alone',
    body:
      'Window and surface cleaning with pressure washing at controlled PSI removes commercial buildup 6× faster than chemical treatment alone. Crystal-clear windows boost curb appeal and foot traffic — results visible within hours of service.',
    tag: 'Windows',
    color: 'var(--accent-blue)',
  },
];

const StatsImpact = forwardRef<HTMLElement>((_, ref) => {
  return (
    <section
      ref={ref}
      id="stats"
      className="reveal-section"
      style={{
        background: 'var(--white)',
        padding: 'clamp(4rem, 8vw, 7rem) clamp(2rem, 6vw, 6rem)',
        borderTop: '1px solid var(--mist-gray)',
      }}
    >
      {/* Section header */}
      <div className="mb-16">
        <div className="flex items-center gap-3 mb-4">
          <div style={{ width: '32px', height: '2px', background: 'var(--accent-blue)' }} />
          <span
            style={{
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'var(--accent-blue)',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
            }}
          >
            § 02 — Scale & Proof
          </span>
        </div>
        <h2
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            color: 'var(--slate)',
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            maxWidth: '600px',
          }}
        >
          Real results.
          <br />
          <span style={{ color: 'var(--accent-blue)' }}>Proven pressure washing.</span>
        </h2>
      </div>
      {/* PSI Stat Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {stats?.map((stat, i) => (
          <div
            key={i}
            className="stat-card"
            style={{ animationDelay: `${i * 0.15}s` }}
          >
            {/* PSI Number */}
            <div
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(2.5rem, 5vw, 3.75rem)',
                color: stat?.color,
                lineHeight: 1,
                letterSpacing: '-0.04em',
                marginBottom: '0.25rem',
              }}
            >
              {stat?.psi}
              {stat?.psiLabel && (
                <span
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    color: 'var(--mist-gray)',
                    marginLeft: '0.25rem',
                    letterSpacing: '0',
                  }}
                >
                  {stat?.psiLabel}
                </span>
              )}
            </div>

            {/* Tag */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'rgba(0,119,204,0.08)',
                color: 'var(--accent-blue)',
                borderRadius: '4px',
                padding: '0.25rem 0.625rem',
                fontSize: '0.625rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                marginBottom: '1rem',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
              }}
            >
              {stat?.tag}
            </div>

            <h3
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontWeight: 600,
                fontSize: '1.0625rem',
                color: 'var(--slate)',
                lineHeight: 1.3,
                marginBottom: '0.875rem',
                letterSpacing: '-0.01em',
              }}
            >
              {stat?.headline}
            </h3>

            <p
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '0.8125rem',
                color: 'var(--mist-gray)',
                lineHeight: 1.65,
              }}
            >
              {stat?.body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
});

StatsImpact.displayName = 'StatsImpact';
export default StatsImpact;